import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion"
import { FileText, Scale, AlertTriangle, Users, CreditCard, Shield, MailIcon } from "lucide-react"
import { HomepageLoader } from "../components/Homepage/HomepageLoader";
import SeoHead from "../seo/SeoHead";
import { generateMetadata } from "../seo/metadataGenerator";
import { PAGE_OG_IMAGES, SITE_URL } from "../seo/constants";
import { generateBreadcrumbSchema, generateGraphSchema, generateWebPageSchema } from "../seo/schemaGenerator";
import { usePrerenderReady } from "../hooks/usePrerenderReady";

export const TermsOfServices = () => {
  usePrerenderReady();
  const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const updateCursorPosition = useCallback((e) => {
    requestAnimationFrame(() => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    });
  }, []);

  useEffect(() => {
    document.body.style.cursor = "none";
    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);

    const interactiveElements = document.querySelectorAll(
      'a, button, input, [role="button"]'
    );
    interactiveElements.forEach((element) => {
      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);
    });

    window.addEventListener("mousemove", updateCursorPosition);

    // Simulate loading time (remove this in production and use real loading logic)
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => {
      clearTimeout(timer);
      document.body.style.cursor = "auto";
      window.removeEventListener("mousemove", updateCursorPosition);
      interactiveElements.forEach((element) => {
        element.removeEventListener("mouseenter", handleMouseEnter);
        element.removeEventListener("mouseleave", handleMouseLeave);
      });
    };
  }, [updateCursorPosition]);
  const metadata = generateMetadata({
    title: "Terms of Service | UpNComer",
    description: "Read the UpNComer Terms of Service governing the use of our platform and services.",
    image: PAGE_OG_IMAGES.helpCenter,
    path: "/terms-of-service",
  });

  const webPageSchema = generateWebPageSchema({
    title: "Terms of Service | UpNComer",
    description: "Read the UpNComer Terms of Service governing the use of our platform and services.",
    url: `${SITE_URL}/terms-of-service`,
  });

  const schemas = generateGraphSchema([
    webPageSchema,
    generateBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Terms of Service", path: "/terms-of-service" },
    ]),
  ]);

  return (
    <>
      <SeoHead metadata={metadata} schemas={schemas} />
      <div
        className={`cursor-spotlight ${isHovering ? "hovering" : ""}`}
        style={{
          left: `${cursorPosition.x}px`,
          top: `${cursorPosition.y}px`,
          transform: `translate(-50%, -50%)`,
        }}
      >
        <div className="inner-circle"></div>
      </div>
      <div className="relative min-h-screen bg-gradient-to-br from-[#000456] via-[#00067E] via-[#0a1a8a] to-[#1e3a8a] overflow-hidden">
        {/* Enhanced Multi-layered Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#000322] via-[#00067E] via-[#0a1a8a] via-[#1e3a8a] to-[#3b82f6] opacity-90" />

          {/* Floating orbs */}
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 10,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
            }}
            className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-[#6366f1]/20 via-[#4f46e5]/15 to-[#FA6138]/10 rounded-full blur-3xl"
          />
        </div>

        <main className="relative z-10 mt-16 md:mt-18 py-16 md:py-20">
          <div className="container max-w-4xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-[#FA6138]/20 rounded-2xl">
                  <Scale className="w-12 h-12 text-[#FA6138]" />
                </div>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-[#c7d2fe] via-[#a5b4fc] to-[#818cf8] bg-clip-text text-transparent mb-6 drop-shadow-lg">
                Terms & Conditions
              </h1>
              <p className="bg-gradient-to-r from-[#818cf8]/80 via-[#6366f1]/85 to-[#4f46e5]/80 bg-clip-text text-transparent text-xl font-light max-w-3xl mx-auto leading-relaxed">
                Please read these Terms carefully before using our services.
              </p>
              <div className="text-[#818cf8]/60 text-sm mt-4">Last updated: January 15, 2025</div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-gradient-to-br from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25 backdrop-blur-xl border border-[#6366f1]/20 rounded-2xl p-8 md:p-12"
              style={{
                boxShadow: "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
              }}
            >
              <div className="prose prose-invert max-w-none">
                <div className="space-y-8">
                  {/* Agreement */}
                  <section>
                    <div className="flex items-center mb-4">
                      <FileText className="w-6 h-6 text-[#FA6138] mr-3" />
                      <h2 className="text-2xl font-bold text-[#c7d2fe]">Agreement to Terms</h2>
                    </div>
                    <p className="text-[#818cf8]/80 leading-relaxed">
                      <b>These Terms and Conditions (“Terms”) form a legally binding agreement between you and UpNComer Pte. Ltd.</b> (“UpNComer,” “we,” “our,” or “us”) governing your access to and use of our website, web and mobile applications, and related products and services (collectively, the “Service”).
                      By using the Service, you agree to these Terms and our Privacy Policy.
                      If you do not agree, you must not use the Service.
                    </p>
                  </section>

                  {/* Eligibility */}
                  <section>
                    <h2 className="text-2xl font-bold text-[#c7d2fe] mb-4">Eligibility</h2>
                    <p className="text-[#818cf8]/80 leading-relaxed mb-4">To use our Service, you must:</p>
                    <ul className="list-disc list-inside text-[#818cf8]/80 space-y-2 ml-4">
                      <li>Be at least <b>18 years old</b> (or have verifiable parental consent)</li>
                      <li>Have <b>legal capacity</b> to enter binding agreements</li>
                      <li>Not be barred from using the Service under applicable laws</li>
                      <li>Provide <b>accurate and complete</b> information when registering</li>
                    </ul>
                  </section>

                  {/* Account Registration */}
                  <section>
                    <h2 className="text-2xl font-bold text-[#c7d2fe] mb-4">Account Registration and Security</h2>
                    <p className="text-[#818cf8]/80 leading-relaxed mb-4">When you create an account, you agree to:</p>
                    <ul className="list-disc list-inside text-[#818cf8]/80 space-y-2 ml-4">
                      <li>Provide accurate, current, and complete details</li>
                      <li>Maintain and promptly update your information</li>
                      <li>Keep your login credentials confidential</li>
                      <li>Notify us immediately of any unauthorized access</li>
                      <li>You are responsible for all activity that occurs under your account.</li>
                    </ul>
                  </section>

                  {/* Service Description */}
                  <section>
                    <h2 className="text-2xl font-bold text-[#c7d2fe] mb-4">Description of Service</h2>
                    <p className="text-[#818cf8]/80 font-bold leading-relaxed mb-4">
                      UpNComer provides a digital ecosystem for independent artists, including:
                    </p>
                    <ul className="list-disc list-inside text-[#818cf8]/80 space-y-2 ml-4">
                      <li><b>Growth Engine:</b> ad-campaign management and automation</li>
                      <li><b>Data Analyst:</b> analytics and performance dashboards</li>
                      <li><b>Amplitude AI:</b> AI-driven recommendations and insights</li>
                      <li><b>Audio Engineer:</b> online mastering integrations</li>
                      <li><b>Artist Manager / Fan Hub:</b> revenue tracking, fan links, and engagement tools</li>
                    </ul>
                    <p className="text-[#818cf8]/80 font-bold leading-relaxed mt-4">
                      We may update, suspend, or discontinue features at any time with or without notice.
                    </p>
                  </section>

                  {/* User Content */}
                  <section>
                    <h2 className="text-2xl font-bold text-[#c7d2fe] mb-4">User Content & Intellectual Property</h2>
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-bold text-[#a5b4fc] mb-2">Your Content</h3>
                        <p className="text-[#818cf8]/80 leading-relaxed">
                          <b>You retain full ownership of your music, artwork, and other creative materials (“User Content”).</b><br />
                          <b>By uploading, you grant UpNComer a non-exclusive, worldwide, royalty-free license</b> to store, process, and display your content <b>solely</b> to provide the Service.
                          We claim no ownership or commercial rights over your works.
                        </p>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-[#a5b4fc] mb-2">Our Content</h3>
                        <p className="text-[#818cf8]/80 leading-relaxed mb-2">
                          <b>All trademarks, logos, and platform materials remain the property of UpNComer Pte. Ltd.</b> or its licensors.<br />
                          You may not copy, modify, or redistribute them without prior written permission.
                        </p>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-[#a5b4fc] mb-2">Content Standards</h3>
                        <p className="text-[#818cf8]/80 leading-relaxed mb-2">
                          You agree not to upload or share content that:
                        </p>
                        <ul className="list-disc list-inside text-[#818cf8]/80 space-y-1 ml-4">
                          <li>Infringes another party’s IP rights</li>
                          <li>Contains unlawful, hateful, or defamatory material</li>
                          <li>Includes malware or malicious code</li>
                          <li>Violates any applicable law or regulation</li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* Payment Terms */}
                  <section>
                    <div className="flex items-center mb-4">
                      <CreditCard className="w-6 h-6 text-[#FA6138] mr-3" />
                      <h2 className="text-2xl font-bold text-[#c7d2fe]">Payment Terms</h2>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-semibold text-[#a5b4fc] mb-2">Subscription Plans</h3>
                        <p className="text-[#818cf8]/80 font-semibold leading-relaxed">
                          Access to paid features is offered through subscription plans.<br />
                          By subscribing, you agree to pay the fees described for your plan.<br />
                          Payments are processed securely by third-party providers (e.g., Stripe).
                        </p>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-[#a5b4fc] mb-2">Billing & Renewals</h3>
                        <ul className="list-disc list-inside text-[#818cf8]/80 space-y-1 ml-4">
                          <li>Fees are <b>billed in advance</b> and are <b>non-refundable</b>, except where required by law.</li>
                          <li>Subscriptions <b>renew automatically</b> at the end of each billing cycle unless cancelled.</li>
                          <li>You may <b>cancel anytime</b> through your account settings.</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-[#a5b4fc] mb-2">In-App Purchases & Credits (One-Time)</h3>
                        <ul className="list-disc list-inside text-[#818cf8]/80 space-y-1 ml-4">
                          <li>Certain features (e.g., credit top-ups, mastering deliveries, campaign boosts, email/SMS sends, or link upgrades) are offered as <b>optional, one-time purchases.</b></li>
                          <li>By clicking <b>“Buy,” “Top Up,” “Confirm Purchase,”</b> or similar, you give <b>express authorization</b> for UpNComer and our payment processor to <b>charge your selected payment method immediately</b> for the displayed amount plus any applicable taxes or fees.</li>
                          <li><b>Delivery/consumption:</b> credits are added to your account instantly; pay-per-use features are delivered or consumed upon purchase or as described at checkout.</li>
                          <li><b>Refunds:</b> one-time purchases are non-refundable once delivered or consumed, except where required by law or explicitly stated at checkout.</li>
                          <li><b>App stores (if applicable):</b> if purchased through the Apple App Store or Google Play, their billing terms and refund policies apply.</li>
                          <li><b>Currency & taxes:</b> unless otherwise shown, charges are in USD and exclusive of GST/VAT/sales tax, which may be added based on your billing address.</li>
                          <li><b>Failed payments/chargebacks:</b> repeated chargebacks or failed payments may lead to suspension of the feature or account.</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-[#a5b4fc] mb-2">Price Changes</h3>
                        <p className="text-[#818cf8]/80 leading-relaxed">
                          <b>We may change pricing with 30 days’ notice.</b><br />
                          Updated pricing takes effect on the next renewal cycle.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Prohibited Uses */}
                  <section>
                    <div className="flex items-center mb-4">
                      <AlertTriangle className="w-6 h-6 text-[#FA6138] mr-3" />
                      <h2 className="text-2xl font-bold text-[#c7d2fe]">Prohibited Uses</h2>
                    </div>
                    <p className="text-[#818cf8]/80 font-semibold leading-relaxed mb-4">
                      You agree not to:
                    </p>
                    <ul className="list-disc list-inside text-[#818cf8]/80 space-y-2 ml-4">
                      <li>Violate any laws or regulations</li>
                      <li>Infringe IP or privacy rights</li>
                      <li>Upload harmful or fraudulent content</li>
                      <li>Attempt to access systems without authorization</li>
                      <li>Interfere with or disrupt the Service</li>
                      <li>Create fake or multiple accounts</li>
                      <li>Engage in spam, scraping, or data harvesting</li>
                      <li>Use the Service for unlawful, abusive, or misleading activity</li>
                    </ul>
                  </section>

                  {/* Termination */}
                  <section>
                    <h2 className="text-2xl font-bold text-[#c7d2fe] mb-4">Termination</h2>
                    <p className="text-[#818cf8]/80 leading-relaxed mb-4">
                      <b>We may suspend or terminate your account at any time for breach of these Terms or misuse of the Service.</b>
                      <b>You may terminate your account by contacting </b>
                      <a href="mailto:support@upncomer.co" className="text-[#FA6138] hover:underline">
                        support@upncomer.co
                      </a>.<br />
                      Upon termination, your right to use the Service ceases immediately.<br />
                      We may delete your data consistent with our Privacy Policy.
                    </p>
                  </section>

                  {/* Disclaimers */}
                  <section>
                    <div className="flex items-center mb-4">
                      <Shield className="w-6 h-6 text-[#FA6138] mr-3" />
                      <h2 className="text-2xl font-bold text-[#c7d2fe]">Disclaimers</h2>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <p className="text-[#818cf8]/80 font-semibold leading-relaxed">
                          The Service is provided “as is” and “as available.”<br />
                          We do not warrant that it will be uninterrupted, error-free, or secure.<br />
                          You assume full responsibility for your use of the Service.
                        </p>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-[#a5b4fc] mb-2">Limitation of Liability</h3>
                        <p className="text-[#818cf8]/80 leading-relaxed">
                          <b>To the fullest extent permitted by law, UpNComer Pte. Ltd.</b>, its affiliates, and employees are <b>not liable</b> for any indirect, incidental, consequential, or punitive damages—including loss of profits, data, or goodwill—arising from your use of the Service.
                          Our total liability for any claim shall not exceed the amount you paid to us in the <b>preceding 12 months</b>.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Indemnification */}
                  <section>
                    <h2 className="text-2xl font-bold text-[#c7d2fe] mb-4">Indemnification</h2>
                    <p className="text-[#818cf8]/80 font-semibold leading-relaxed mb-4">
                      <b>You agree to indemnify and hold harmless UpNComer Pte. Ltd.</b>, its officers, employees, and partners from any claims, losses, or damages resulting from:
                    </p>
                    <ul className="list-disc list-inside text-[#818cf8]/80 space-y-2 ml-4">
                      <li>Your violation of these Terms</li>
                      <li>Your misuse of the Service</li>
                      <li>Your infringement of third-party rights</li>
                    </ul>
                  </section>

                  {/* Governing Law */}
                  <section>
                    <h2 className="text-2xl font-bold text-[#c7d2fe] mb-4">Governing Law and Dispute Resolution</h2>
                    <p className="text-[#818cf8]/80 leading-relaxed mb-4">
                      <b>These Terms are governed by the laws of Singapore</b>, without regard to conflict-of-law principles.<br />
                      Any dispute will first be resolved through <b>good-faith negotiation.</b><br />
                      If unresolved, the matter shall be submitted to <b>binding arbitration in Singapore</b>, conducted in English under the <b>Singapore International Arbitration Centre (SIAC)</b> Rules.
                    </p>
                  </section>

                  {/* Changes to Terms */}
                  <section>
                    <h2 className="text-2xl font-bold text-[#c7d2fe] mb-4">Changes to These Terms</h2>
                    <p className="text-[#818cf8]/80 font-semibold leading-relaxed">
                      We may modify these Terms from time to time.<br />
                      Updates will appear on this page with a revised “Last updated” date.<br />
                      Material changes will be communicated via email or in-app notice.<br />
                      Your continued use of the Service after such changes constitutes acceptance.
                    </p>
                  </section>

                  {/* Contact Information */}
                  <section>
                    <h2 className="text-2xl font-bold text-[#c7d2fe] mb-4">Contact Us</h2>
                    <div className="bg-[#1e3a8a]/20 rounded-lg p-4 border border-[#3b82f6]/30">
                      <p className="text-[#c7d2fe] mb-2">
                        <strong>UpNComer Pte. Ltd.</strong>
                      </p>
                      <p className="text-[#818cf8]/80">6 Marina Boulevard, 11-18, Singapore 018985</p>
                      <p className="text-[#818cf8]/80 flex items-center gap-2">
                        <MailIcon />
                        <a
                          href="mailto:support@upncomer.co"
                          className="text-[#FA6138] hover:underline"
                        >
                          support@upncomer.co
                        </a>
                      </p>
                    </div>
                  </section>
                </div>
              </div>
            </motion.div>
          </div>
        </main>
      </div>
    </>
  );
};
